import { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";

import MatterLoader from "../Loader/MatterLoader";
import CreateWorkItemModal from "../Inbox/CreateWorkItemModal";
import { WorkItemCard } from "../WorkItems/MatterWorkPanel";
import {
  getMatterWorkItems,
  getMatterWorkItemContext,
} from "../../service/workItems";

/**
 * The matter dashboard's Work Items card (Matthew, 8/31: the Tasks
 * section should offer "Create work item and then I get the options").
 *
 * Renders only when this matter has migrated to the new Salesforce —
 * an unmigrated matter keeps its old Tasks card with Create Task, and
 * this component quietly reports availability up so the parent can
 * apply the One Door rule (work items live → Create Task retires,
 * same as the inbox).
 *
 * Rows are the same WorkItemCard the Home lanes and the AI pane's Work
 * panel use — expand for the instruction, thread, and this user's
 * verbs, in place. Create Work Item opens the same composer the inbox
 * uses (type options → subject, instruction, the matter team as
 * recipients), just anchored to the matter instead of a document.
 */

const Card = styled.section`
  position: relative;
  display: flex;
  flex-direction: column;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  padding: 12px 14px 8px;
  min-height: 150px;
  min-width: 0;
`;

const Head = styled.header`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f1f2f4;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #374151;
`;

const Count = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #8a93a3;
`;

const CreateButton = styled.button`
  margin-left: auto;
  border: 1px solid #d1d5db;
  background: #fff;
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 12px;
  cursor: pointer;

  &:hover {
    color: #0c53bb;
    border-color: #0c53bb;
  }
`;

const Empty = styled.div`
  padding: 22px 2px;
  text-align: center;
  font-size: 12px;
  color: #9099a8;
`;

const FooterLink = styled.button`
  align-self: flex-start;
  border: none;
  background: none;
  padding: 8px 2px 4px;
  font-size: 12px;
  font-weight: 600;
  color: #0c53bb;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

/** Enough to see the live volleys; the Work panel has the rest. */
const MAX_ROWS = 8;

const MatterWorkItemsCard = ({ matterId, onAvailability }) => {
  const navigate = useNavigate();
  const [loadStatus, setLoadStatus] = useState("loading");
  const [items, setItems] = useState([]);
  const [available, setAvailable] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  // A slow earlier matter's answer must never overwrite the current one.
  const requestSeq = useRef(0);

  const load = useCallback(() => {
    if (!matterId) return;
    const seq = ++requestSeq.current;
    setLoadStatus("loading");
    Promise.all([
      getMatterWorkItemContext(matterId),
      getMatterWorkItems(matterId),
    ])
      .then(([ctxResponse, workResponse]) => {
        if (seq !== requestSeq.current) return;
        const ctx = ctxResponse?.data || {};
        const live = Boolean(ctx.enabled && ctx.matter_found);
        setAvailable(live);
        if (onAvailability) onAvailability(live);
        setItems(workResponse?.data?.work_items || []);
        setLoadStatus("ready");
      })
      .catch(() => {
        if (seq !== requestSeq.current) return;
        // Unreachable Salesforce: leave the dashboard alone — the old
        // Tasks card still stands, nothing to render here.
        setAvailable(false);
        if (onAvailability) onAvailability(false);
        setLoadStatus("failed");
      });
  }, [matterId, onAvailability]);

  useEffect(() => {
    load();
  }, [load]);

  if (!available) return null;

  const shown = items.slice(0, MAX_ROWS);
  const overflow = items.length - shown.length;

  return (
    <Card aria-label="Work Items" data-testid="matter-work-items-card">
      {loadStatus === "loading" && (
        <MatterLoader message="Loading..." offsetTop={0} absolute={true} />
      )}

      <Head>
        <Title>Work Items</Title>
        <Count>({items.length})</Count>
        <CreateButton type="button" onClick={() => setCreateOpen(true)}>
          Create Work Item
        </CreateButton>
      </Head>

      {shown.length === 0 ? (
        <Empty>No open work items on this matter</Empty>
      ) : (
        shown.map((item) => (
          <WorkItemCard key={item.id} item={item} onActed={load} />
        ))
      )}

      <FooterLink
        type="button"
        onClick={() => navigate(`/matter/${matterId}?ai=work`)}
      >
        {overflow > 0
          ? `Open the Work panel — ${overflow} more`
          : "Open the Work panel"}
      </FooterLink>

      <CreateWorkItemModal
        open={createOpen}
        matterId={matterId}
        onClose={() => setCreateOpen(false)}
        onCreated={load}
      />
    </Card>
  );
};

MatterWorkItemsCard.propTypes = {
  matterId: PropTypes.string,
  /** Reports whether work items are live on this matter, so the parent
   * can retire Create Task (One Door). */
  onAvailability: PropTypes.func,
};

export default MatterWorkItemsCard;
